/**
 * Reveal assistant text one character at a time.
 * The first sentence stays slow. A long backlog catches up so a list does not crawl.
 * @param {{ onText: (id: string, text: string | null) => void, intervalMs?: number }} args
 */
export function createTextReveal({ onText, intervalMs = 18 }) {
  const jobs = [];
  const shownById = new Map();
  let timer = null;

  function stepCount(shown, target) {
    const remaining = target.length - shown;
    if (remaining <= 0) return 0;
    const sentence = target.slice(0, 240).search(/[.!?](\s|$)/);
    const calmUntil =
      sentence === -1 ? Math.min(80, target.length) : Math.min(sentence + 1, target.length);
    if (shown < calmUntil) return 1;
    if (remaining > 160) return 12;
    if (remaining > 40) return 4;
    return 2;
  }

  function dropJob(job) {
    const idx = jobs.indexOf(job);
    if (idx !== -1) jobs.splice(idx, 1);
  }

  function complete(job) {
    dropJob(job);
    if (job.release) {
      shownById.delete(job.id);
      onText(job.id, null);
      return;
    }
    shownById.set(job.id, job.shown);
    onText(job.id, job.target.slice(0, job.shown));
  }

  function tick() {
    const job = jobs[0];
    if (!job) {
      timer = null;
      return;
    }
    const n = stepCount(job.shown, job.target);
    if (n === 0) {
      complete(job);
    } else {
      job.shown = Math.min(job.target.length, job.shown + n);
      shownById.set(job.id, job.shown);
      if (job.shown >= job.target.length) complete(job);
      else onText(job.id, job.target.slice(0, job.shown));
    }
    if (jobs.length) timer = setTimeout(tick, intervalMs);
    else timer = null;
  }

  function kick() {
    if (!timer && jobs.length) tick();
  }

  return {
    /**
     * @param {string} id
     * @param {string} text
     * @param {{ release?: boolean }} [opts] release clears the overlay once the text is fully shown
     */
    start(id, text, opts = {}) {
      const target = String(text || "");
      if (!target) return;
      let shown = shownById.get(id) || 0;
      if (shown > target.length) shown = target.length;
      const existing = jobs.find((job) => job.id === id);
      if (existing) {
        existing.target = target;
        existing.shown = Math.min(existing.shown, target.length);
        if (opts.release) existing.release = true;
        shownById.set(id, existing.shown);
        kick();
        return;
      }
      if (shown >= target.length) {
        if (opts.release) {
          shownById.delete(id);
          onText(id, null);
        } else {
          shownById.set(id, shown);
          onText(id, target);
        }
        return;
      }
      const job = {
        id,
        target,
        shown,
        release: Boolean(opts.release),
      };
      jobs.push(job);
      shownById.set(id, shown);
      if (shown === 0 && jobs[0] !== job) onText(id, "");
      kick();
    },
    /** Let a held reply fall back to its stored text once typing has caught up. */
    release(id) {
      const job = jobs.find((item) => item.id === id);
      if (job) {
        job.release = true;
        if (job.shown >= job.target.length) complete(job);
        return;
      }
      if (shownById.has(id)) {
        shownById.delete(id);
        onText(id, null);
      }
    },
    /** Replace whatever was typing with a new string from the first character. */
    restart(id, text) {
      const target = String(text || "");
      shownById.set(id, 0);
      const existing = jobs.find((job) => job.id === id);
      if (existing) {
        existing.target = target;
        existing.shown = 0;
        existing.release = true;
        if (jobs[0] !== existing) {
          dropJob(existing);
          jobs.unshift(existing);
        }
      } else if (target) {
        jobs.unshift({ id, target, shown: 0, release: true });
      }
      kick();
    },
    stop() {
      if (timer) clearTimeout(timer);
      timer = null;
      jobs.length = 0;
      shownById.clear();
    },
  };
}
